import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CloseOutlined, ApiOutlined } from '@ant-design/icons';
import { Button, Flex, Form, Input, Select, Tabs, Typography, Space, Row, Col, message, Modal } from 'antd';
import axios from '../../../axios-interceptor';
import { setWorkflow, setSelectedStep } from '../../../redux/slices/connectorSlice';
import { trimStringValues } from '../../../utils/trimStrings';
import { useTranslation } from 'react-i18next';

const { TextArea } = Input;

const HttpRequestPanel = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { selectedStep, workflow } = useSelector(state => state.connector);
  const [form] = Form.useForm();
  const [pasteVisible, setPasteVisible] = useState(false);
  const [pasteValue, setPasteValue] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState({ open: false, title: t('Test Result'), content: '', status: undefined });

  const isHttpRequestStep = !!(selectedStep?.id && (selectedStep.data?.type === 'httpRequest' || selectedStep.type === 'httpRequest'));
  const nodeId = selectedStep?.id;
  const methodWatch = Form.useWatch('method', form);
  const authTypeWatch = Form.useWatch('authType', form);
  const paramsKVWatch = Form.useWatch('paramsKV', form);

  useEffect(() => {
    if (!isHttpRequestStep || !nodeId) return;
    const node = (workflow.nodes || []).find(n => n.id === nodeId);
    const http = node?.data?.http || {};
  const initial = {
      name: node?.data?.name || 'HTTP Request',
      method: http.method || 'GET',
      url: http.url || '',
      authType: http.auth?.type || 'none',
      authToken: http.auth?.token || '',
      headersKV: http.headersKV || parseHeaders(http.headers),
      paramsKV: http.paramsKV || parseParams(http.params) || [],
      bodyType: http.body?.type || 'json',
      body: http.body?.content || '',
  };
  if (!initial.headersKV || initial.headersKV.length === 0) initial.headersKV = [{ key: '', value: '' }];
  if (!initial.paramsKV || initial.paramsKV.length === 0) initial.paramsKV = [{ key: '', value: '' }];
  form.setFieldsValue(initial);
  }, [isHttpRequestStep, nodeId, workflow.nodes, form]);

  useEffect(() => {
    const container = document.getElementById('http-request-container');
    if (container) {
      container.style.display = isHttpRequestStep ? 'block' : 'none';
    }
  }, [isHttpRequestStep]);

  const onClose = () => dispatch(setSelectedStep({}));

  const onSave = async () => {
    const values = trimStringValues(await form.validateFields());
    // Validate JSON when applicable
    if (values.bodyType === 'json' && values.body) {
      try {
        JSON.parse(values.body);
      } catch (e) {
        message.error(t('Body must be valid JSON'));
        return;
      }
    }

    // Auto add JSON content-type header if bodyType=json and not present
    let headersKV = (values.headersKV || []).filter(kv => kv && (kv.key || kv.value));
    if (values.bodyType === 'json') {
      const hasCT = headersKV.some(kv => kv.key?.toLowerCase() === 'content-type');
      if (!hasCT) headersKV = headersKV.concat([{ key: 'Content-Type', value: 'application/json' }]);
    }

    const paramsKV = (values.paramsKV || []).filter(kv => kv && (kv.key || kv.value));
    const headersText = serializeHeaders(headersKV);
    const paramsText = serializeParams(paramsKV);
    const nextNodes = (workflow.nodes || []).map(n => {
      if (n.id !== nodeId) return { ...n };
      return {
        ...n,
        data: {
          ...n.data,
          type: 'httpRequest',
          name: values.name || n.data?.name || 'HTTP Request',
          http: {
            method: values.method,
            url: values.url,
            auth: { type: values.authType, token: values.authToken },
            headers: headersText,
            headersKV,
            params: paramsText,
            paramsKV,
            body: { type: values.bodyType, content: values.body },
          },
        },
      };
    });
    dispatch(setWorkflow({ ...workflow, nodes: nextNodes }));
    onClose();
  };

  // Execute request without saving
  const onTest = async () => {
    try {
      const values = await form.validateFields([
        'method', 'url', 'paramsKV', 'headersKV', 'bodyType', 'body', 'authType', 'authToken'
      ]);

      // Build URL with params
      const paramsKV = (values.paramsKV || []).filter(kv => kv && (kv.key || kv.value));
      const qs = serializeParams(paramsKV);
      let url = values.url || '';
      if (qs) url = url.includes('?') ? `${url}&${qs}` : `${url}?${qs}`;

      // Build headers
      const headers = {};
      (values.headersKV || []).forEach(kv => {
        if (kv?.key) headers[kv.key] = kv.value || '';
      });
      if (values.authType === 'bearer' && values.authToken) {
        headers['Authorization'] = `Bearer ${values.authToken}`;
      }

      // Body
      let data;
      const method = String(values.method || 'GET').toUpperCase();
      const allowBody = !['GET', 'HEAD'].includes(method);
      if (allowBody && values.body) {
        if (values.bodyType === 'json') {
          try {
            data = JSON.parse(values.body);
            if (!Object.keys(headers).some(h => h.toLowerCase() === 'content-type')) {
              headers['Content-Type'] = 'application/json';
            }
          } catch (e) {
            message.error(t('Body must be valid JSON'));
            return;
          }
        } else if (values.bodyType === 'form') {
          const params = new URLSearchParams();
          (values.body || '').split('&').map(s => s.trim()).filter(Boolean).forEach(p => {
            const i = p.indexOf('=');
            const k = i > -1 ? p.slice(0, i) : p;
            const v = i > -1 ? p.slice(i + 1) : '';
            params.append(k, v);
          });
          data = params;
          if (!Object.keys(headers).some(h => h.toLowerCase() === 'content-type')) {
            headers['Content-Type'] = 'application/x-www-form-urlencoded';
          }
        } else {
          data = values.body; // raw
        }
      }

      setTesting(true);
  const resp = await axios({ method, url, headers, data });
  const preview = typeof resp?.data === 'string' ? resp.data : JSON.stringify(resp?.data, null, 2);
  setTestResult({ open: true, title: `Test Result • ${resp?.status ?? ''}`.trim(), content: preview || '', status: resp?.status });
    } catch (err) {
      const status = err?.status || err?.response?.status;
      const data = err?.data || err?.response?.data;
  const preview = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
  setTestResult({ open: true, title: `Test Failed${status ? ' • ' + status : ''}`, content: preview || String(err), status });
    } finally {
      setTesting(false);
    }
  };

  if (!isHttpRequestStep) return null;

  // helpers for K/V editing and previews
  function parseHeaders(text) {
    if (!text) return [];
    return String(text)
      .split('\n')
      .map(l => l.trim())
      .filter(Boolean)
      .map(line => {
        const idx = line.indexOf(':');
        return idx > -1
          ? { key: line.slice(0, idx).trim(), value: line.slice(idx + 1).trim() }
          : { key: line.trim(), value: '' };
      });
  }

  function serializeHeaders(list = []) {
    return list
      .filter(kv => kv && (kv.key || kv.value))
      .map(kv => `${kv.key || ''}: ${kv.value || ''}`.trim())
      .join('\n');
  }

  function parseParams(text) {
    if (!text) return [];
    // support key=value lines or a=1&b=2
    const arr = [];
    const ampersand = String(text).includes('&') && !String(text).includes('\n');
    const lines = ampersand ? String(text).split('&') : String(text).split('\n');
    lines
      .map(s => s.trim())
      .filter(Boolean)
      .forEach(line => {
        const idx = line.indexOf('=');
        if (idx > -1) arr.push({ key: line.slice(0, idx).trim(), value: line.slice(idx + 1).trim() });
        else arr.push({ key: line, value: '' });
      });
    return arr;
  }

  function serializeParams(list = []) {
    return list
      .filter(kv => kv && (kv.key || kv.value))
      .map(kv => `${kv.key || ''}=${kv.value || ''}`)
      .join('&');
  }

  const urlWithParams = (() => {
    const url = form.getFieldValue('url') || '';
    const paramsKV = (paramsKVWatch || form.getFieldValue('paramsKV')) || [];
    const qs = serializeParams(paramsKV);
    if (!qs) return url;
    if (!url) return `?${qs}`;
    return url.includes('?') ? `${url}&${qs}` : `${url}?${qs}`;
  })();

  const bodyDisabled = ['GET', 'HEAD'].includes((methodWatch || form.getFieldValue('method') || '').toUpperCase());

  const pasteToken = async () => {
    // Try direct clipboard read in secure contexts; otherwise show fallback modal
    try {
      if (window.isSecureContext && navigator.clipboard?.readText) {
        const text = await navigator.clipboard.readText();
        if (text) {
          form.setFieldsValue({ authToken: text });
          return;
        }
      }
      setPasteVisible(true);
    } catch (e) {
      setPasteVisible(true);
    }
  };

  return (
    <>
    <div className="step-details-container" id="http-request-container">
      <Flex className='step-details-header' align='center' justify='space-between'>
        <Typography.Title level={5} className='margin-0'>
          <Space>
            <ApiOutlined />
            {t('HTTP Request')}
          </Space>
        </Typography.Title>
        <CloseOutlined className='icon-medium' onClick={onClose} />
      </Flex>
      <div className='step-details-body'>
        <Form form={form} layout='vertical' onFinish={onSave}>
          <Form.Item label={t('Request Name')} name='name' rules={[{ required: true, whitespace: true, message: 'Name is required' }]}>
            <Input placeholder={t('My API call')} />
          </Form.Item>
          <Flex gap={8} style={{ marginBottom: 12 }}>
            <Form.Item name='method' style={{ width: 140, marginBottom: 0 }}>
              <Select
                options={['GET','POST','PUT','PATCH','DELETE','HEAD','OPTIONS'].map(v=>({label:v,value:v}))}
              />
            </Form.Item>
            <Form.Item name='url' style={{ flex: 1, marginBottom: 0 }} rules={[{ required: true, whitespace: true, message: 'URL is required' }] }>
              <Input placeholder='https://api.example.com/resource' />
            </Form.Item>
          </Flex>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>{t('Preview:')} {urlWithParams || '—'}</div>

          <Tabs
            items={[
              {
                key: 'auth',
                label: t('Auth'),
                children: (
                  <Flex gap={12} vertical>
                    <Form.Item label={t('Type')} name='authType'>
                      <Select options={[{label:t('None'),value:'none'},{label:t('Bearer Token'),value:'bearer'}]} />
                    </Form.Item>
                    {(authTypeWatch || form.getFieldValue('authType')) === 'bearer' && (
                      <Form.Item label={t('Bearer Token')} name='authToken' rules={[{ required: true, whitespace: true, message: 'Token is required' }] }>
                        <Input.Password placeholder={t('Paste token')} addonAfter={<Button type='link' onClick={pasteToken}>{t('Paste')}</Button>} />
                      </Form.Item>
                    )}
                  </Flex>
                )
              },
              {
                key: 'headers',
                label: t('Headers'),
                children: (
                  <Form.List name='headersKV'>
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...rest }) => (
                          <Row gutter={8} key={key} style={{ marginBottom: 8 }}>
                            <Col span={11}>
                              <Form.Item {...rest} name={[name, 'key']}>
                                <Input placeholder={t('Header')} />
                              </Form.Item>
                            </Col>
                            <Col span={11}>
                              <Form.Item {...rest} name={[name, 'value']}>
                                <Input placeholder={t('Value')} />
                              </Form.Item>
                            </Col>
                            <Col span={2}>
                              <Button onClick={() => remove(name)} danger>×</Button>
                            </Col>
                          </Row>
                        ))}
                        <Button type='dashed' onClick={() => add()} block>{t('Add header')}</Button>
                      </>
                    )}
                  </Form.List>
                )
              },
              {
                key: 'params',
                label: t('Params'),
                children: (
                  <Form.List name='paramsKV'>
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...rest }) => (
                          <Row gutter={8} key={key} style={{ marginBottom: 8 }}>
                            <Col span={11}>
                              <Form.Item {...rest} name={[name, 'key']}>
                                <Input placeholder='key' />
                              </Form.Item>
                            </Col>
                            <Col span={11}>
                              <Form.Item {...rest} name={[name, 'value']}>
                                <Input placeholder='value' />
                              </Form.Item>
                            </Col>
                            <Col span={2}>
                              <Button onClick={() => remove(name)} danger>×</Button>
                            </Col>
                          </Row>
                        ))}
                        <Button type='dashed' onClick={() => add()} block>{t('Add param')}</Button>
                      </>
                    )}
                  </Form.List>
                )
              },
              {
                key: 'body',
                label: t('Body'),
                children: (
                  <>
                    <Form.Item label={t('Type')} name='bodyType'>
                      <Select options={[{label:'JSON',value:'json'},{label:t('Raw'),value:'raw'},{label:t('Form-URL Encoded'),value:'form'}]} />
                    </Form.Item>
                     <Form.Item name='body'>
                       <TextArea rows={8} placeholder='{"key":"value"}' disabled={bodyDisabled} />
                    </Form.Item>
                  </>
                )
              }
            ]}
          />

          <Form.Item className='step-details-action'>
            <Flex gap={12}>
              <Button onClick={onTest} loading={testing} style={{ width: '9.75rem' }}>{t('Test')}</Button>
              <Button type='primary' htmlType='submit' className='submit-button' style={{ width: '9.75rem' }}>{t('Save')}</Button>
              <Button onClick={onClose} style={{ width: '9.75rem' }}>{t('Cancel')}</Button>
            </Flex>
          </Form.Item>
        </Form>
      </div>
    </div>
    <Modal
      title={t('Paste Bearer Token')}
      open={pasteVisible}
      onOk={() => { form.setFieldsValue({ authToken: pasteValue }); setPasteVisible(false); setPasteValue(''); }}
      onCancel={() => { setPasteVisible(false); setPasteValue(''); }}
      okText={t('Use Token')}
    >
      <Input.Password
        autoFocus
        placeholder={t('Paste token here')}
        value={pasteValue}
        onChange={(e) => setPasteValue(e.target.value)}
      />
      <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>{t('Tip: Press Ctrl/Cmd + V to paste.')}</div>
    </Modal>
    <Modal
      title={testResult.title}
      open={testResult.open}
      footer={<Button onClick={() => setTestResult(prev => ({ ...prev, open: false }))}>{t('Close')}</Button>}
      onCancel={() => setTestResult(prev => ({ ...prev, open: false }))}
      width={720}
    >
      <pre style={{ maxHeight: 360, overflow: 'auto', background: '#0b1021', color: '#d6deeb', padding: 12, borderRadius: 6 }}>
        {testResult.content || 'No content'}
      </pre>
    </Modal>
    </>
  );
};

export default HttpRequestPanel;
